import * as React from "react";
import ReactDOM from "react-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, Input, Select } from "@jhool-io/fe-components";
import { INewClientDetails, UserSex } from "../../../utils/types/client";
import {
    PHONE_REGEX,
    normalizePhoneNumber,
} from "../../../utils/helpers/phonenumber/phonenumber";
import {
    handleFormatDatePickerValue,
    makeStringFirstLetterCapital,
} from "../../../utils/helpers";
import { useFetchUserDetails } from "../../../hooks/queries/user";

type SubmitTypes = "save" | "save_continue";

interface AddPersonalInfoProps {
    onFormSubmit: (payload: INewClientDetails) => void;
    submitType: SubmitTypes | null;
}

const schema = yup.object().shape({
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required"),
    date_of_birth: yup.date().required("Date of birth is required"),
    contact: yup
        .string()
        .required("Contact is required")
        .matches(PHONE_REGEX, "Field should only contain numbers"),
    sex: yup.string().required("Sex is required"),
});

type Option = {
    label: React.ReactNode;
    value: boolean;
};

const getSexForSelect = Object.values(UserSex).map((sex) => ({
    label: makeStringFirstLetterCapital(sex),
    value: sex,
}));

export default function AddPersonalInfo({
    onFormSubmit,
    submitType,
}: AddPersonalInfoProps) {
    // Local component states
    const [localDateOfBirth, setLocalDateOfBirth] = React.useState<Date | null>(
        null
    );

    // Get looged in user data
    const loggedInUser = useFetchUserDetails();

    // React hook form values
    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
        watch,
        setValue,
    } = useForm<INewClientDetails>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    // Function that handles mutation of data and form submit action in general
    const onSubmit = (data: INewClientDetails) => {
        const dataToSend = {
            ...data,
            practice_id: loggedInUser.data?.practices[0]?.practice_id || "",
            date_of_birth: handleFormatDatePickerValue(data.date_of_birth),
            primary_contact: undefined,
        };

        onFormSubmit(dataToSend);
    };

    // Watch for when contact values in our form change
    const contact = watch("contact");

    // Run helper to format contact values when it change
    React.useEffect(() => {
        if (contact) {
            setValue("contact", normalizePhoneNumber(contact));
        }
    }, [contact, setValue]);

    return (
        <form
            id={
                submitType === "save"
                    ? "add-personal-info-save"
                    : "add-personal-info-continue"
            }
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="fg fg-space-between two flex">
                <Input
                    {...register("first_name")}
                    label="First name"
                    placeholder="First name"
                    hasError={!!errors.first_name}
                    errorText={errors.first_name?.message}
                    isRequired
                />
                <Input
                    {...register("last_name")}
                    label="Last name"
                    placeholder="Last name"
                    hasError={!!errors.last_name}
                    errorText={errors.last_name?.message}
                    isRequired
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="date_of_birth"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Date of Birth"
                            onChange={(date) => {
                                field.onChange(date);
                                setLocalDateOfBirth(date);
                            }}
                            selected={localDateOfBirth}
                            maxDate={new Date(Date.now())}
                            hasError={!!errors.date_of_birth}
                            errorText={
                                errors.date_of_birth?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.date_of_birth?.message
                            }
                            isRequired
                            popperClassName="!z-[50]"
                            popperContainer={({ children }) =>
                                ReactDOM.createPortal(children, document.body)
                            }
                            popperProps={{
                                strategy: "fixed",
                                modifiers: [
                                    {
                                        name: "zIndex",
                                        options: {
                                            zIndex: 50,
                                        },
                                    },
                                ],
                            }}
                        />
                    )}
                />
                <Input
                    {...register("contact")}
                    label="Contact"
                    placeholder="Contact"
                    hasError={!!errors.contact}
                    errorText={errors.contact?.message}
                    isRequired
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="sex"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Sex"
                            placeholder="Sex"
                            options={getSexForSelect}
                            onChange={(val) =>
                                field.onChange((val as Option).value)
                            }
                            hasError={!!errors.sex}
                            errorText={errors.sex?.message}
                            isLongListInDialog
                            isRequired
                        />
                    )}
                />
                <Input
                    {...register("email")}
                    label="Email"
                    placeholder="Email"
                    hasError={!!errors.email}
                    errorText={errors.email?.message}
                    isRequired
                />
            </div>
        </form>
    );
}
